/**
 * Persona Avatar Pool — deterministic photo allocation with **per-debate
 * uniqueness guarantee**.
 *
 * Mirrors `services/macdm-orchestrator/src/macdm/orchestrator/avatar_pool.py`.
 * Both sides hash the persona id and modulo into a 15-slot, gender-aware
 * pool of curated 3D portraits served from `/avatars/`.
 *
 * Two public surfaces:
 *
 *   1. `resolveAvatarUrl(persona)`
 *      — Single-persona lookup. Used as the legacy/standalone fallback when
 *        no allocation context exists (e.g. preview cards, isolated
 *        wizard tiles). Two personas with colliding hashes WILL get the
 *        same face here — by design, because there is no "other persona"
 *        to compare against.
 *
 *   2. `assignUniqueAvatars(personas)`
 *      — Batch allocator. Given the full council of a debate, returns a
 *        `Map<personaId, avatarUrl>` such that **no two personas share a
 *        face**, even when their hashes collide. This is the function the
 *        runtime stage and PDF report use.
 *
 * Algorithm (uniqueness):
 *   - Group personas by gender bucket ("male" | "female").
 *   - Sort each bucket by `persona.id` (deterministic — guarantees the
 *     same allocation across reloads, replays and PDF re-renders).
 *   - For each persona compute the FNV-1a "preferred" slot, then linearly
 *     probe forward (mod POOL_SIZE) until an unclaimed slot is found.
 *   - If the bucket is over-subscribed (>15 of same gender), spill into
 *     the opposite gender's pool with the same probing scheme — chosen
 *     over recycling because a face mismatch (man → woman) is visually
 *     more obvious than a recycled face, and the council size cap is 9
 *     today, so a real spill is improbable.
 *
 * Algorithm (legacy single-shot):
 *   - `slot = (fnv1a(persona.id) % POOL_SIZE) + 1`
 *   - URL: `/avatars/{male|female}-NN.png`
 */

import type { Persona } from "../domain/types";

const POOL_SIZE = 15;
const BUCKETS = ["male", "female"] as const;
type Bucket = (typeof BUCKETS)[number];

function fnv1a(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

function genderBucket(persona: Persona): Bucket {
  if (persona.gender === "female") return "female";
  if (persona.gender === "male") return "male";
  if (persona.honorific === "Hanım") return "female";
  return "male";
}

function buildUrl(bucket: Bucket, slot: number): string {
  return `/avatars/${bucket}-${String(slot).padStart(2, "0")}.png`;
}

/**
 * Single-persona deterministic resolution. **Does NOT guarantee uniqueness
 * against other personas** — call `assignUniqueAvatars` for the batch case.
 *
 * Honors `persona.avatarUrl` when the backend has already stamped one
 * (uniqueness-aware backend allocator runs at provision time, see
 * `avatar_pool.py::assign_unique_avatars`).
 *
 * Hardening (2026-05): Backend stamped URL'i pool sınırı içinde
 * (`/avatars/(male|female)-(01..15).png`) DOĞRULAR. Eski bir backend
 * sürümünden veya bug'lı kayıttan POOL_SIZE üstü slot gelirse (örn.
 * `female-20.png`) hint reddedilir, FNV-1a hash ile yeniden hesaplanır.
 * Bu sayede 404 broken-image durumu sıfırlanır.
 */
export function resolveAvatarUrl(persona: Persona): string {
  const stamped = persona.avatarUrl?.trim();
  if (stamped && isValidPoolUrl(stamped)) {
    return stamped;
  }
  const slot = (fnv1a(persona.id) % POOL_SIZE) + 1;
  return buildUrl(genderBucket(persona), slot);
}

/** True if URL points to a slot inside the on-disk pool (1..POOL_SIZE). */
export function isValidPoolUrl(url: string): boolean {
  const m = /\/avatars\/(male|female)-(\d{2})\.png$/.exec(url);
  if (!m) return false;
  const slot = Number(m[2]);
  return slot >= 1 && slot <= POOL_SIZE;
}

/**
 * Allocate a **unique** avatar URL for every persona in the council.
 *
 * Returns `Map<personaId, avatarUrl>`. Personas WITHOUT an `id` are
 * skipped silently (defensive — never observed in practice).
 *
 * Backend-stamped `avatarUrl` is treated as a *hint*: if it is unique
 * within the supplied set, it is honored. Otherwise it is dropped and
 * the slot is reassigned via probing — this keeps the FE robust against
 * legacy data (snapshots, replays, debug fixtures) where the backend
 * never ran the unique allocator.
 */
export function assignUniqueAvatars(personas: readonly Persona[]): Map<string, string> {
  const out = new Map<string, string>();

  // Phase 1 — group by bucket, deterministic order.
  const byBucket: Record<Bucket, Persona[]> = { male: [], female: [] };
  for (const p of personas) {
    if (!p?.id) continue;
    byBucket[genderBucket(p)].push(p);
  }
  for (const b of BUCKETS) {
    byBucket[b].sort((a, c) => (a.id < c.id ? -1 : a.id > c.id ? 1 : 0));
  }

  // Phase 2 — global occupancy table so cross-bucket spillover doesn't
  // collide. Key: `${bucket}|${slot}` → personaId.
  const occupied = new Map<string, string>();
  const claim = (bucket: Bucket, slot: number, personaId: string): boolean => {
    const key = `${bucket}|${slot}`;
    if (occupied.has(key)) return false;
    occupied.set(key, personaId);
    return true;
  };

  // Phase 3 — for each persona, prefer the backend-stamped URL if present
  // AND still free; else linearly probe its preferred bucket; else spill.
  for (const bucket of BUCKETS) {
    for (const persona of byBucket[bucket]) {
      const preferredSlot = (fnv1a(persona.id) % POOL_SIZE) + 1;

      let assignedBucket: Bucket = bucket;
      let assignedSlot = -1;

      // Honor backend hint when free.
      const stamped = persona.avatarUrl?.trim();
      if (stamped) {
        const m = /\/avatars\/(male|female)-(\d{2})\.png$/.exec(stamped);
        if (m) {
          const hintBucket = m[1] as Bucket;
          const hintSlot = Number(m[2]);
          if (
            hintSlot >= 1 &&
            hintSlot <= POOL_SIZE &&
            claim(hintBucket, hintSlot, persona.id)
          ) {
            assignedBucket = hintBucket;
            assignedSlot = hintSlot;
          }
        }
      }

      // Probe preferred bucket (forward, wrap).
      if (assignedSlot === -1) {
        for (let i = 0; i < POOL_SIZE; i += 1) {
          const slot = ((preferredSlot - 1 + i) % POOL_SIZE) + 1;
          if (claim(bucket, slot, persona.id)) {
            assignedBucket = bucket;
            assignedSlot = slot;
            break;
          }
        }
      }

      // Spill into opposite bucket if oversubscribed.
      if (assignedSlot === -1) {
        const spill: Bucket = bucket === "male" ? "female" : "male";
        for (let i = 0; i < POOL_SIZE; i += 1) {
          const slot = ((preferredSlot - 1 + i) % POOL_SIZE) + 1;
          if (claim(spill, slot, persona.id)) {
            assignedBucket = spill;
            assignedSlot = slot;
            break;
          }
        }
      }

      // Last-resort: pool fully exhausted (>30 personas — never happens
      // under our 9-seat cap). Fall back to legacy hash so the avatar
      // disc is at least populated (and accept duplication).
      if (assignedSlot === -1) {
        assignedSlot = preferredSlot;
        assignedBucket = bucket;
      }

      out.set(persona.id, buildUrl(assignedBucket, assignedSlot));
    }
  }

  return out;
}

/** Pool size — exported so consumers can sanity-check the on-disk fixture set. */
export const AVATAR_POOL_SIZE = POOL_SIZE;
